/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import chevronRight from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-right_16';
import upToTop from '@ba-ui-toolkit/ba-graphics/dist/icons-js/up-to-top_16.js';
import Button from '@ba-ui-toolkit/button';
import { ListItem } from '@ba-ui-toolkit/list';
import HSpacer from '@ba-ui-toolkit/hspacer';
import { FlexItem } from '@ba-ui-toolkit/flexlayout';
import { lodash, event } from '@ba-ui-toolkit/utils';

import styles from './TreeItem.scss';

const treeItemClassName = styles.treeItemClassName.replace(/"/g, '');
const { omit } = lodash;
const { stopEvent } = event;

export default class TreeItem extends Component {
	static propTypes = {
		depth: PropTypes.number.isRequired,
		length: PropTypes.number.isRequired,
		index: PropTypes.number.isRequired,
		icon: PropTypes.any,
		selected: PropTypes.bool,
		active: PropTypes.bool,
		children: PropTypes.node.isRequired,
		onMenu: PropTypes.func,
		alwaysExpanded: PropTypes.bool,
		expanded: PropTypes.bool,
		onExpand: PropTypes.func,
		hasChildren: PropTypes.bool,
		expandTitle: PropTypes.string,
		pathToRoot: PropTypes.string,
		ellipsisTitle: PropTypes.string,
		isFocusNode: PropTypes.bool,
	};

	static defaultProps = {
		depth: 0,
	};

	_onExpand = e => {
		stopEvent(e);
		const { onExpand, index, expanded } = this.props;
		onExpand && onExpand(index, !expanded);
	};

	// This is needed so that we can expand without changing selection
	_stopPropagation = e => {
		e.stopPropagation();
	};

	_onMenu = (x, y, e) => {
		const { onMenu } = this.props;
		onMenu && onMenu(x, y, e);
	};

	render() {
		const {
			icon,
			depth,
			alwaysExpanded,
			hasChildren,
			expanded,
			children,
			onMenu,
			index,
			expandTitle,
			pathToRoot,
			isFocusNode,
			...other
		} = this.props;

		let indent = icon ? 1 : 0;

		if (alwaysExpanded) {
			indent += 2;
		} else if (!hasChildren) {
			indent += 4;
		}

		if (depth != null) {
			indent += depth * 2;
		}

		const passThroughProps = omit(
			other,
			'visible',
			'node',
			'getSelectedNodes',
			'data',
			'dragging',
			'loading',
			'onExpand',
			'pathToRoot'
		);

		return (
			<ListItem
				{...passThroughProps}
				index={index}
				onMenu={onMenu && this._onMenu}
				className={`${treeItemClassName}`}
				role="treeitem"
				aria-expanded={hasChildren ? expanded : null}
				data-tid={`treeItem${pathToRoot}`}
			>
				{icon}
				{indent > 0 ? <HSpacer size={indent} /> : null}
				{(hasChildren && !alwaysExpanded) || isFocusNode ? (
					<FlexItem>
						<Button
							button={false}
							title={expandTitle}
							size="small"
							onMouseDown={this._stopPropagation}
							onTouchEnd={this._stopPropagation}
							onDoubleClick={this._stopPropagation}
							onClick={this._onExpand}
							variant="inline"
							icon={
								<SVGIcon
									bidi
									pad
									className={`${treeItemClassName}__button`}
									iconId={isFocusNode ? upToTop.id : chevronRight.id}
									size="small"
									rotate={expanded && !isFocusNode ? 90 : 0}
								/>
							}
							data-tid="expandTreeItemClickable"
						/>
					</FlexItem>
				) : null}
				<FlexItem grow className={`${treeItemClassName}__child-item`}>
					{children}
				</FlexItem>
			</ListItem>
		);
	}
}
